import { useState, useEffect, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calculator, ArrowRight, Share2, Check, MapPin, Loader2 } from "lucide-react";
import { QUOTE } from "@/constants/testIds";
import { toast } from "sonner";

const homeSizes = [
  { value: "studio", label: "Studio" },
  { value: "1br", label: "1 Bedroom" },
  { value: "2br", label: "2 Bedroom" },
  { value: "3br", label: "3 Bedroom" },
  { value: "4br_plus", label: "4+ Bedroom" },
  { value: "office", label: "Office / Commercial" },
];
const moveTypes = [
  { value: "local", label: "Local Move" },
  { value: "long_distance", label: "Long Distance" },
  { value: "freight_container", label: "Load/Unload Container or Trailer" },
  { value: "storage", label: "Storage Only" },
  { value: "commercial", label: "Commercial" },
  { value: "specialty", label: "Specialty Items" },
  { value: "packing", label: "Packing Only" },
];
const extras = [
  { id: "packing", label: "Packing Service", testid: QUOTE.packing },
  { id: "unpacking", label: "Unpacking Service", testid: QUOTE.unpacking },
  { id: "piano", label: "Piano Moving", testid: QUOTE.piano },
  { id: "antiques", label: "Antiques / Art", testid: QUOTE.antiques },
  { id: "storage_30", label: "30-day Storage", testid: QUOTE.storage30 },
  { id: "stairs", label: "Stairs / Walk-Up (+$20)", testid: "quote-extra-stairs" },
];

export default function QuoteCalculator() {
  const [homeSize, setHomeSize] = useState("2br");
  const [moveType, setMoveType] = useState("local");
  const [distance, setDistance] = useState("25");
  const [services, setServices] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shared, setShared] = useState(false);
  const [originCity, setOriginCity] = useState("");
  const [originState, setOriginState] = useState("KY");
  const [destCity, setDestCity] = useState("");
  const [destState, setDestState] = useState("KY");
  const [distanceLoading, setDistanceLoading] = useState(false);
  const autoRanRef = useRef(false);

  const toggleService = (id) =>
    setServices((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const autoFillDistance = async () => {
    if (!originCity.trim() || !destCity.trim()) {
      toast.error("Enter origin & destination cities first");
      return;
    }
    setDistanceLoading(true);
    try {
      const { data } = await api.post("/distance", {
        origin_city: originCity.trim(),
        origin_state: originState.trim().toUpperCase(),
        destination_city: destCity.trim(),
        destination_state: destState.trim().toUpperCase(),
      });
      if (data.miles == null) {
        toast.error("Couldn't find those cities — check spelling or use nearest big city");
        return;
      }
      setDistance(String(data.miles));
      toast.success(`Distance auto-filled: ${data.miles} miles`);
    } catch {
      toast.error("Distance lookup failed. Enter miles manually.");
    } finally {
      setDistanceLoading(false);
    }
  };

  const fetchQuote = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/quote", payload);
      setResult(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not calculate quote");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await fetchQuote({
      home_size: homeSize,
      move_type: moveType,
      distance_miles: Number(distance) || 0,
      extra_services: services,
    });
  };

  // Read shared-quote params from the URL on mount + auto-run quote
  useEffect(() => {
    if (autoRanRef.current) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("q")) return;
    const hs = params.get("h") || "2br";
    const mt = params.get("m") || "local";
    const d = params.get("d") || "0";
    const svc = (params.get("s") || "").split(",").filter(Boolean);
    setHomeSize(hs);
    setMoveType(mt);
    setDistance(d);
    setServices(svc);
    autoRanRef.current = true;
    // Scroll into view + run
    setTimeout(() => {
      document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
      fetchQuote({
        home_size: hs,
        move_type: mt,
        distance_miles: Number(d) || 0,
        extra_services: svc,
      });
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildShareUrl = () => {
    const base = `${window.location.origin}/`;
    const params = new URLSearchParams({
      q: "1",
      h: homeSize,
      m: moveType,
      d: String(distance || 0),
      s: services.join(","),
    });
    return `${base}?${params.toString()}#quote`;
  };

  const handleShare = async () => {
    const url = buildShareUrl();
    const rangeText = result
      ? `$${result.low.toLocaleString()} – $${result.high.toLocaleString()}`
      : "";
    const shareData = {
      title: "Them Boys Moving Quote",
      text: `My ThemBoysMoving quote: ${rangeText}. Check it out:\n\n— Shared via Them Boys Moving & Storage · (502) 507-7904`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        // user dismissed — fall through to clipboard
        if (e && e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setShared(false), 2500);
    } catch {
      toast.error("Couldn't copy — long-press the address bar to share manually");
    }
  };

  return (
    <section id="quote" className="py-24 lg:py-32 bg-[#EBE8DD]" data-testid="quote-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <div className="overline">Instant Estimate</div>
          <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Get a real number<span className="text-[#5BB239]">.</span> In 60 seconds.
          </h2>
          <p className="mt-6 text-[#5C635F] leading-relaxed max-w-md">
            No phone tag. No salesman. Tell us what you're moving and we'll give you an honest
            range — based on real prices from real moves.
          </p>

          <div className="mt-10 space-y-5">
            <Bullet n="01" label="Pick your home size" />
            <Bullet n="02" label="Tell us the type of move" />
            <Bullet n="03" label="Add any extras" />
            <Bullet n="04" label="See your price range" />
          </div>
        </div>

        <div className="lg:col-span-3">
          <form
            onSubmit={submit}
            className="bg-[#F5F3EC] border border-[#DDD8C9] p-6 lg:p-10 space-y-6"
          >
            <div className="flex items-center gap-2 pb-4 border-b border-[#DDD8C9]">
              <Calculator className="w-4 h-4 text-[#5BB239]" />
              <span className="font-mono-label text-xs">Quote Calculator</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="font-mono-label text-[10px]">Home Size</Label>
                <Select value={homeSize} onValueChange={setHomeSize}>
                  <SelectTrigger className="mt-2 rounded-none border-[#DDD8C9] h-12" data-testid={QUOTE.homeSize}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {homeSizes.map((h) => (
                      <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-label text-[10px]">Move Type</Label>
                <Select value={moveType} onValueChange={setMoveType}>
                  <SelectTrigger className="mt-2 rounded-none border-[#DDD8C9] h-12" data-testid={QUOTE.moveType}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {moveTypes.map((h) => (
                      <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="font-mono-label text-[10px]">Distance (miles)</Label>
                <button
                  type="button"
                  onClick={() => document.getElementById("route-picker")?.classList.toggle("hidden")}
                  className="font-mono-label text-[10px] text-[#5BB239] hover:text-[#499828] flex items-center gap-1"
                  data-testid="quote-toggle-route-picker"
                >
                  <MapPin className="w-3 h-3" /> Not sure? Auto-fill from cities
                </button>
              </div>
              <Input
                type="number"
                min="0"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                className="mt-2 rounded-none border-[#DDD8C9] h-12"
                data-testid={QUOTE.distance}
              />
              <div
                id="route-picker"
                className="mt-3 border border-dashed border-[#5BB239]/40 bg-[#5BB239]/5 p-4 hidden"
                data-testid="route-picker-block"
              >
                <div className="font-mono-label text-[9px] text-[#5BB239] tracking-widest mb-2">
                  Enter Cities · We&apos;ll Look Up The Miles
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Origin city"
                    value={originCity}
                    onChange={(e) => setOriginCity(e.target.value)}
                    className="col-span-2 rounded-none border-[#DDD8C9] h-10 text-sm"
                    data-testid="quote-origin-city"
                  />
                  <Input
                    placeholder="ST"
                    value={originState}
                    onChange={(e) => setOriginState(e.target.value.toUpperCase().slice(0, 2))}
                    maxLength={2}
                    className="rounded-none border-[#DDD8C9] h-10 text-sm uppercase text-center"
                    data-testid="quote-origin-state"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Input
                    placeholder="Destination city"
                    value={destCity}
                    onChange={(e) => setDestCity(e.target.value)}
                    className="col-span-2 rounded-none border-[#DDD8C9] h-10 text-sm"
                    data-testid="quote-dest-city"
                  />
                  <Input
                    placeholder="ST"
                    value={destState}
                    onChange={(e) => setDestState(e.target.value.toUpperCase().slice(0, 2))}
                    maxLength={2}
                    className="rounded-none border-[#DDD8C9] h-10 text-sm uppercase text-center"
                    data-testid="quote-dest-state"
                  />
                </div>
                <button
                  type="button"
                  onClick={autoFillDistance}
                  disabled={distanceLoading}
                  className="mt-2 w-full bg-[#151816] hover:bg-[#2a2f2c] text-white h-10 font-mono-label text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="quote-autofill-distance"
                >
                  {distanceLoading ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up…</>
                  ) : (
                    <><MapPin className="w-3.5 h-3.5 text-[#5BB239]" /> Calculate Distance</>
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label className="font-mono-label text-[10px]">Add-on services</Label>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {extras.map((ex) => (
                  <label
                    key={ex.id}
                    className="flex items-center gap-3 border border-[#DDD8C9] p-3 cursor-pointer hover:bg-white transition-colors"
                  >
                    <Checkbox
                      checked={services.includes(ex.id)}
                      onCheckedChange={() => toggleService(ex.id)}
                      className="rounded-none"
                      data-testid={ex.testid}
                    />
                    <span className="text-sm">{ex.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#5BB239] text-white py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#499828] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid={QUOTE.submit}
            >
              {loading ? "Calculating…" : "Calculate Estimate"}
              <ArrowRight className="w-4 h-4" />
            </button>

            {result && (
              <div className="border-t border-[#DDD8C9] pt-6 bg-[#223932] -mx-6 lg:-mx-10 px-6 lg:px-10 py-6 text-white">
                <div className="font-mono-label text-[10px] text-[#5BB239]">
                  {result.model === "hourly" ? "Estimated Total (Hourly)" : "Estimated Range"}
                </div>
                <div className="mt-2 flex items-baseline gap-3 font-display font-black tracking-tight">
                  <span className="text-4xl lg:text-5xl" data-testid={QUOTE.resultLow}>${result.low.toLocaleString()}</span>
                  <span className="text-2xl opacity-50">—</span>
                  <span className="text-4xl lg:text-5xl" data-testid={QUOTE.resultHigh}>${result.high.toLocaleString()}</span>
                </div>
                {result.model === "hourly" ? (
                  <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                    <div className="bg-white/5 px-3 py-2">
                      <div className="font-mono-label text-[9px] text-white/50">Crew</div>
                      <div className="mt-0.5 font-bold">{result.crew} movers</div>
                    </div>
                    <div className="bg-white/5 px-3 py-2">
                      <div className="font-mono-label text-[9px] text-white/50">Rate</div>
                      <div className="mt-0.5 font-bold">${result.hourly_rate}/hr</div>
                    </div>
                    <div className="bg-white/5 px-3 py-2">
                      <div className="font-mono-label text-[9px] text-white/50">Est. Hours</div>
                      <div className="mt-0.5 font-bold">
                        {result.estimated_hours.low}–{result.estimated_hours.high} hr
                      </div>
                    </div>
                    {result.mileage_fee > 0 && (
                      <div className="col-span-3 bg-white/5 px-3 py-2 flex items-center justify-between">
                        <div className="font-mono-label text-[9px] text-white/50">
                          Mileage Fee · $2/mi after 10 mi
                        </div>
                        <div className="font-bold">+${result.mileage_fee}</div>
                      </div>
                    )}
                  </div>
                ) : null}
                <p className="mt-3 text-xs text-white/70 max-w-md">
                  {result.model === "hourly"
                    ? `Hourly rate · ${result.min_hours}-hour minimum. Final based on actual time on-site.`
                    : "Estimate based on home size, move type, distance, and add-ons. Final quote confirmed after a quick walk-through."}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a
                    href="#book"
                    className="inline-flex items-center gap-2 bg-[#5BB239] text-white px-5 py-3 font-bold uppercase tracking-wider text-xs hover:bg-[#499828] transition-colors"
                  >
                    Lock In My Date <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={handleShare}
                    data-testid="quote-share-btn"
                    className="inline-flex items-center gap-2 border border-white/30 text-white px-5 py-3 font-bold uppercase tracking-wider text-xs hover:bg-white/10 transition-colors"
                  >
                    {shared ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-[#5BB239]" />
                        Link Copied
                      </>
                    ) : (
                      <>
                        <Share2 className="w-3.5 h-3.5" />
                        Share My Quote
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Bullet({ n, label }) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="font-mono-label text-[10px] text-[#5BB239] w-6">{n}</span>
      <span className="font-display font-bold text-lg">{label}</span>
    </div>
  );
}
